import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import WordListLayout from '../../../../shared/reusables/WordListLayout';
import { fetchWords } from '../../../../shared/api/darija';

/**
 * VerbWordList — alphabetical overview of all verbs.
 * Verbs have a unique "darijaBase" answer key, so this is kept separate from GenericWordList.
 */
export default function VerbWordList({ onBack }) {
    const [data, setData] = useState([]);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchWords('verbs')
            .then((verbs) =>
                setData([...verbs].sort((a, b) => a.dutch.localeCompare(b.dutch, 'nl')))
            )
            .catch((err) => {
                console.error('[VerbWordList]:', err);
                setError(err.message || 'Ophalen mislukt.');
            });
    }, []);

    return (
        <WordListLayout
            title="Werkwoorden"
            subtitle={
                error
                    ? 'Fout bij laden'
                    : data.length
                    ? `${data.length} werkwoorden · alfabetisch`
                    : 'Laden…'
            }
            accentColor="#6c63ff"
            accentColor2="#2575fc"
            label="Woordenlijst"
            onBack={onBack}
        >
            {error && (
                <Typography sx={{ color: '#f87171', fontFamily: "'DM Sans', sans-serif", textAlign: 'center', mt: 4 }}>
                    ⚠️ {error}
                </Typography>
            )}

            {!error && data.length > 0 && (
                <Box
                    sx={{
                        background: 'rgba(255,255,255,0.03)',
                        backdropFilter: 'blur(12px)',
                        border: '1px solid rgba(255,255,255,0.08)',
                        borderRadius: '16px',
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: 2,
                            px: { xs: 2, sm: 3 },
                            py: 1.2,
                            borderBottom: '1px solid rgba(255,255,255,0.08)',
                            background: 'rgba(255,255,255,0.03)',
                        }}
                    >
                        {['Nederlands', 'Darija'].map((col) => (
                            <Typography
                                key={col}
                                sx={{
                                    color: '#7878a0',
                                    fontFamily: "'DM Sans', sans-serif",
                                    fontSize: '0.72rem',
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.1em',
                                }}
                            >
                                {col}
                            </Typography>
                        ))}
                    </Box>

                    {data.map((verb, idx) => (
                        <motion.div
                            key={verb.dutch}
                            initial={{ opacity: 0, x: -8 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: Math.min(idx * 0.01, 0.35), duration: 0.22 }}
                        >
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr',
                                    gap: 2,
                                    px: { xs: 2, sm: 3 },
                                    py: 1.5,
                                    borderBottom: idx < data.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none',
                                    '&:hover': { background: 'rgba(108,99,255,0.06)' },
                                    transition: 'background 0.15s',
                                }}
                            >
                                <Typography sx={{ color: '#e8e8f0', fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.93rem' }}>
                                    {verb.dutch}
                                </Typography>
                                <Typography sx={{ color: '#9d97ff', fontFamily: "'DM Sans', sans-serif", fontSize: '0.93rem', fontWeight: 500 }}>
                                    {verb.darijaBase}
                                </Typography>
                            </Box>
                        </motion.div>
                    ))}
                </Box>
            )}
        </WordListLayout>
    );
}
